import { getNotificationsApi } from './api';

export const NOTIFICATIONS_FEED_CACHE_TTL_MS = 30 * 1000;

const DEFAULT_NOTIFICATIONS_FEED_CACHE_SCOPE = 'anonymous';
const cachedFirstPageResponses = new Map();
const inFlightNotificationFeedRequests = new Map();

const normalizeCacheScope = cacheScope => {
  const normalized = String(cacheScope || '').trim();
  return normalized || DEFAULT_NOTIFICATIONS_FEED_CACHE_SCOPE;
};

const normalizeResponse = response => {
  const items = Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response?.notifications)
    ? response.notifications
    : [];
  const totalCount =
    Number(response?.totalCount || response?.total || items.length) || items.length;

  return {
    ...response,
    items,
    notifications: items,
    unreadCount: Number(response?.unreadCount || 0),
    nextCursor: response?.nextCursor || null,
    hasMore: !!response?.hasMore,
    total: totalCount,
    totalCount,
  };
};

const cloneResponse = response => {
  const normalized = normalizeResponse(response);
  return {
    ...normalized,
    items: [...normalized.items],
    notifications: [...normalized.notifications],
  };
};

const sliceFirstPageResponse = (response, limit, meta = {}) => {
  const normalized = normalizeResponse(response);

  if (normalized.items.length <= limit) {
    return {
      ...cloneResponse(normalized),
      meta,
    };
  }

  const items = normalized.items.slice(0, limit);
  const hasMore = normalized.totalCount > items.length;
  const nextCursor = hasMore && items.length > 0 ? items[items.length - 1].$id || null : null;

  return {
    ...normalized,
    items,
    notifications: items,
    hasMore,
    nextCursor,
    meta,
  };
};

const getCachedFirstPageEntry = cacheScope => {
  return cachedFirstPageResponses.get(normalizeCacheScope(cacheScope)) || null;
};

const getCacheAgeMs = cacheScope => {
  const entry = getCachedFirstPageEntry(cacheScope);
  if (!entry?.cachedAt) {
    return Number.POSITIVE_INFINITY;
  }

  return Date.now() - entry.cachedAt;
};

const isCachedFirstPageFresh = (
  cacheScope,
  maxAgeMs = NOTIFICATIONS_FEED_CACHE_TTL_MS
) => {
  return !!getCachedFirstPageEntry(cacheScope)?.response && getCacheAgeMs(cacheScope) < maxAgeMs;
};

const deriveFirstPageResponseFromCache = ({ limit, cacheScope }) => {
  const entry = getCachedFirstPageEntry(cacheScope);
  if (!entry || !isCachedFirstPageFresh(cacheScope)) {
    return null;
  }

  return sliceFirstPageResponse(entry.response, limit, {
    source: 'cache',
    cacheHit: true,
    dedupeHit: false,
  });
};

const setCachedFirstPageResponse = ({ response, limit, cacheScope }) => {
  const normalized = normalizeResponse(response);
  const scope = normalizeCacheScope(cacheScope);
  const cachedEntry = getCachedFirstPageEntry(scope);

  if (!cachedEntry || limit >= cachedEntry.limit) {
    cachedFirstPageResponses.set(scope, {
      response: normalized,
      limit,
      cachedAt: Date.now(),
    });
  }

  return normalized;
};

const buildRequestKey = ({ limit, cursor, forceFresh, cacheScope }) => {
  return `${normalizeCacheScope(cacheScope)}:${forceFresh ? 'fresh' : 'default'}:${limit}:${
    cursor || 'first-page'
  }`;
};

const getInFlightRequest = requestKey => {
  return inFlightNotificationFeedRequests.get(requestKey) || null;
};

const getCompatibleInFlightFirstPageRequest = ({
  limit,
  cacheScope,
  requireFresh = false,
}) => {
  const scope = normalizeCacheScope(cacheScope);
  let compatibleRequest = null;

  inFlightNotificationFeedRequests.forEach(entry => {
    if (
      !entry ||
      entry.cacheScope !== scope ||
      entry.cursor ||
      entry.limit < limit ||
      (requireFresh && !entry.forceFresh)
    ) {
      return;
    }

    if (!compatibleRequest) {
      compatibleRequest = entry;
      return;
    }

    if (entry.forceFresh && !compatibleRequest.forceFresh) {
      compatibleRequest = entry;
      return;
    }

    if (entry.forceFresh === compatibleRequest.forceFresh && entry.limit < compatibleRequest.limit) {
      compatibleRequest = entry;
    }
  });

  return compatibleRequest;
};

const logNotificationFeed = payload => {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  // eslint-disable-next-line no-console
  console.info('[notifications] client-feed', payload);
};

export const clearNotificationsFeedCache = (cacheScope = null) => {
  if (cacheScope == null) {
    cachedFirstPageResponses.clear();
    inFlightNotificationFeedRequests.clear();
    return;
  }

  const scope = normalizeCacheScope(cacheScope);
  cachedFirstPageResponses.delete(scope);

  Array.from(inFlightNotificationFeedRequests.keys()).forEach(requestKey => {
    if (requestKey.startsWith(`${scope}:`)) {
      inFlightNotificationFeedRequests.delete(requestKey);
    }
  });
};

export const fetchNotificationsFeed = ({
  limit = 100,
  cursor = null,
  reason = 'unspecified',
  forceFresh = false,
  cacheScope = DEFAULT_NOTIFICATIONS_FEED_CACHE_SCOPE,
} = {}) => {
  const requestedLimit = Number(limit) || 20;
  const normalizedCacheScope = normalizeCacheScope(cacheScope);

  if (!cursor) {
    const compatibleInFlightRequest = getCompatibleInFlightFirstPageRequest({
      limit: requestedLimit,
      cacheScope: normalizedCacheScope,
      requireFresh: !!forceFresh,
    });

    if (compatibleInFlightRequest) {
      logNotificationFeed({
        reason,
        source: 'compatible-in-flight',
        limit: requestedLimit,
        forceFresh,
      });

      return compatibleInFlightRequest.promise.then(response =>
        sliceFirstPageResponse(response, requestedLimit, {
          source: compatibleInFlightRequest.forceFresh
            ? 'compatible-in-flight-fresh'
            : 'compatible-in-flight',
          cacheHit: false,
          dedupeHit: true,
        })
      );
    }
  }

  if (!cursor && !forceFresh) {
    const cached = deriveFirstPageResponseFromCache({
      limit: requestedLimit,
      cacheScope: normalizedCacheScope,
    });
    if (cached) {
      logNotificationFeed({
        reason,
        source: 'cache',
        count: cached.items.length,
        unreadCount: cached.unreadCount,
      });
      return Promise.resolve(cached);
    }
  }

  const requestKey = buildRequestKey({
    limit: requestedLimit,
    cursor,
    forceFresh,
    cacheScope: normalizedCacheScope,
  });

  const inFlightRequest = getInFlightRequest(requestKey);
  if (inFlightRequest) {
    logNotificationFeed({
      reason,
      source: 'in-flight-dedupe',
      limit: requestedLimit,
      cursor,
    });
    return inFlightRequest.promise;
  }

  logNotificationFeed({
    phase: 'started',
    reason,
    source: 'network',
    limit: requestedLimit,
    cursor,
    forceFresh,
  });

  const request = getNotificationsApi({
    limit: requestedLimit,
    cursor,
    reason,
    fresh: forceFresh,
  })
    .then(response => {
      const normalized = normalizeResponse(response);

      if (!cursor) {
        setCachedFirstPageResponse({
          response: normalized,
          limit: requestedLimit,
          cacheScope: normalizedCacheScope,
        });
      }

      logNotificationFeed({
        phase: 'completed',
        reason,
        source: normalized?.source || 'appwrite-db',
        count: normalized.items.length,
        unreadCount: normalized.unreadCount,
      });

      return {
        ...normalized,
        meta: {
          source: normalized?.source || 'appwrite-db',
          cacheHit: false,
          dedupeHit: false,
        },
      };
    })
    .finally(() => {
      inFlightNotificationFeedRequests.delete(requestKey);
    });

  inFlightNotificationFeedRequests.set(requestKey, {
    promise: request,
    limit: requestedLimit,
    cursor,
    forceFresh: !!forceFresh,
    cacheScope: normalizedCacheScope,
  });
  return request;
};

export const applyNotificationReadToCachedFeed = ({
  notificationId,
  updatedNotification = null,
  unreadCount = null,
  cacheScope = DEFAULT_NOTIFICATIONS_FEED_CACHE_SCOPE,
}) => {
  const scope = normalizeCacheScope(cacheScope);
  const cachedEntry = getCachedFirstPageEntry(scope);
  if (!cachedEntry?.response) {
    return;
  }

  let didUpdate = false;
  const nowIso = new Date().toISOString();
  const normalized = normalizeResponse(cachedEntry.response);
  const items = normalized.items.map(item => {
    if (item.$id !== notificationId) {
      return item;
    }

    didUpdate = true;
    return {
      ...item,
      ...(updatedNotification || {}),
      isRead: true,
      readAt: updatedNotification?.readAt || item.readAt || nowIso,
    };
  });

  if (!didUpdate) {
    return;
  }

  cachedFirstPageResponses.set(scope, {
    response: {
      ...normalized,
      items,
      notifications: items,
      unreadCount:
        typeof unreadCount === 'number'
          ? unreadCount
          : Math.max(0, Number(normalized.unreadCount || 0) - 1),
    },
    limit: cachedEntry.limit,
    cachedAt: Date.now(),
  });
};

export const applyMarkAllReadToCachedFeed = ({
  unreadCount = 0,
  cacheScope = DEFAULT_NOTIFICATIONS_FEED_CACHE_SCOPE,
} = {}) => {
  const scope = normalizeCacheScope(cacheScope);
  const cachedEntry = getCachedFirstPageEntry(scope);
  if (!cachedEntry?.response) {
    return;
  }

  const nowIso = new Date().toISOString();
  const normalized = normalizeResponse(cachedEntry.response);
  const items = normalized.items.map(item =>
    item?.isRead
      ? item
      : {
          ...item,
          isRead: true,
          readAt: item.readAt || nowIso,
        }
  );

  cachedFirstPageResponses.set(scope, {
    response: {
      ...normalized,
      items,
      notifications: items,
      unreadCount: Number(unreadCount) || 0,
    },
    limit: cachedEntry.limit,
    cachedAt: Date.now(),
  });
};
